import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMobile } from '../../../contexts/MobileContext';

const TooltipPortal = ({ children, tooltip, show }) => {
    const { isMobile } = useMobile();
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const EDGE_MARGIN_PX = 10;

    useEffect(() => {
        if (isMobile) return; // Do not calculate or attach listeners on mobile

        const updatePosition = () => {
            if (!show || !triggerRef.current) return;
            const rect = triggerRef.current.getBoundingClientRect();
            const baseLeft = rect.left + rect.width / 2;

            let computedLeft = baseLeft;
            const top = rect.bottom + 6;

            if (tooltipRef.current) {
                const width = tooltipRef.current.offsetWidth || 0;
                const minLeft = EDGE_MARGIN_PX + width / 2;
                const maxLeft = (window.innerWidth || 0) - EDGE_MARGIN_PX - width / 2;
                computedLeft = Math.min(Math.max(baseLeft, minLeft), maxLeft);
            }

            setPosition({ top, left: computedLeft });
        };

        updatePosition();

        if (show) {
            window.addEventListener('resize', updatePosition);
            return () => window.removeEventListener('resize', updatePosition);
        }
    }, [show, tooltip, isMobile]);

    const tooltipElement = show && tooltip && !isMobile ? (
        <div
            ref={tooltipRef}
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                transform: 'translateX(-50%)',
                background: '#1a1a1a',
                color: 'var(--color-white)',
                padding: '0.4rem 0.55rem',
                borderRadius: '0.5rem',
                fontSize: '12px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 9999,
                opacity: 1,
                transition: 'opacity 0.12s ease',
            }}
        >
            {tooltip}
        </div>
    ) : null;

    return (
        <>
            <span ref={triggerRef}>
                {children}
            </span>
            {tooltipElement && createPortal(tooltipElement, document.body)}
        </>
    );
};

export default TooltipPortal;
